import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  CurrencyInput,
  Loader,
  Option,
  RadioButton,
  RadioButtonGroup,
  Slider,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Fns, Scoring, ScoringJobPageValidation } from 'src/front/api/apiInstance';
import { LegalEntity, ReferenceDataType, ScoringJobPageVisibility } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import {
  dictionaryLineFormat,
  formatAmount,
  formatBlank,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import {
  isInnNotRequiredOccupation,
  isNotJobOccupation,
  ScoringInformationSchema,
} from 'src/front/common/utils/validationSchemas';

import {
  Container,
  CustomLabel,
  Form,
  FormElement,
  FormRow,
  Separator,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';
import { BREAK_POINTS } from 'src/front/constant';
import styled from 'styled-components';
import useDebounce from 'src/front/common/hooks/useDebounce';
import SuggestionDropdown from 'src/front/components/SuggestionDropdown';

const LineFormElement = styled(FormElement)`
  width: 685px;
`;

const TwoComboContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MonthContainer = styled(FormElement)`
  width: 200px;

  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) and (min-width: ${BREAK_POINTS.MIN
      .PHONE}) {
    width: 180px;
    margin-right: 10px;
  }
`;

const YearContainer = styled(FormElement)`
  width: 120px;
`;

export enum OrganizationType {
  STATE = '1',
  NON_STATE = '2',
}

interface FormData {
  companyName: string;
  workInn: string;
  jobTitle: Option | null;
  occupation: Option | null;
  jobSector: Option | null;
  businessLine: Option | null;
  jobNumberForLast3Years: string;
  employeeNumber: Option | null;
  is2NdflCertificateProvided: Option | null;
  currentWorkBeginningMonth: Option | null;
  currentWorkBeginningYear: Option | null;
  workLifeBeginningMonth: Option | null;
  workLifeBeginningYear: Option | null;
  isGovernmentOrganisation: OrganizationType;

  requestedLimit: string;
  cardLimitPercentage: number;
  income: string;
  otherIncome: string;
}

const defaultValues = {
  companyName: '',
  workInn: '',
  jobTitle: null,
  occupation: null,
  jobSector: null,
  businessLine: null,
  jobNumberForLast3Years: '',
  employeeNumber: null,
  is2NdflCertificateProvided: null,
  currentWorkBeginningMonth: null,
  currentWorkBeginningYear: null,
  workLifeBeginningMonth: null,
  workLifeBeginningYear: null,
  isGovernmentOrganisation: OrganizationType.NON_STATE,

  requestedLimit: '',
  cardLimitPercentage: 100,
  income: '',
  otherIncome: '',
};

interface Props {
  customerId?: string;
  goToNextStep: (customerId?: string) => void;
  disablePage?: boolean;
}

const ScoringInformation: FC<Props> = ({ customerId, goToNextStep, disablePage }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [limitDisplay, setLimitDisplay] = useState<ScoringJobPageVisibility>();

  const jobTitles = usePRM(ReferenceDataType.JOBTITLE);
  const occupations = usePRM(ReferenceDataType.OCCUPATION);
  const jobSectors = usePRM(ReferenceDataType.JOBSECTOR);
  const businessLines = usePRM(ReferenceDataType.BUSSINESSLINE);
  const employeeNumbers = usePRM(ReferenceDataType.EMPLOYEENUMBER);
  const yesNoComboItems = usePRM(ReferenceDataType.YESNOTYPE);
  const months = usePRM(ReferenceDataType.MONTHS);
  const years = usePRM(ReferenceDataType.PASTYEARS);
  const [entitySuggestions, setEntitySuggestions] = useState<LegalEntity[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { handleSubmit, errors, setValue, control, formState, trigger, watch } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(ScoringInformationSchema, {
      abortEarly: false,
      context: { limitDisplay },
    }),
  });

  const loadScoringInformation = useCallback(async (): Promise<void> => {
    const { data: response } = await ScoringJobPageValidation.getScoringJobPageData();
    setLimitDisplay(response.displayRules);
    if (response.result === 'OK' && response.pageData) {
      const {
        companyName,
        workInn,
        jobTitle,
        occupation,
        jobSector,
        businessLine,
        jobNumberForLast3Years,
        employeeNumber,
        is2NdflCertificateProvided,
        requestedLimit,
        cardLimitPercentage,
        income,
        otherIncome,
        isGovernmentOrganisation,
        workLifeBeginningMonth,
        workLifeBeginningYear,
        currentWorkBeginningMonth,
        currentWorkBeginningYear,
      } = response.pageData;
      [
        { name: 'companyName', value: companyName },
        { name: 'workInn', value: workInn },
        { name: 'jobTitle', value: optionFormat(jobTitle) },
        { name: 'occupation', value: optionFormat(occupation) },
        { name: 'jobSector', value: optionFormat(jobSector) },
        { name: 'businessLine', value: optionFormat(businessLine) },
        { name: 'jobNumberForLast3Years', value: jobNumberForLast3Years },
        { name: 'employeeNumber', value: optionFormat(employeeNumber) },
        { name: 'is2NdflCertificateProvided', value: optionFormat(is2NdflCertificateProvided) },
        { name: 'workLifeBeginningMonth', value: optionFormat(workLifeBeginningMonth) },
        { name: 'workLifeBeginningYear', value: optionFormat(workLifeBeginningYear) },
        { name: 'currentWorkBeginningMonth', value: optionFormat(currentWorkBeginningMonth) },
        { name: 'currentWorkBeginningYear', value: optionFormat(currentWorkBeginningYear) },
        {
          name: 'isGovernmentOrganisation',
          value: isGovernmentOrganisation ? OrganizationType.STATE : OrganizationType.NON_STATE,
        },
        { name: 'requestedLimit', value: formatAmount(requestedLimit) },
        { name: 'cardLimitPercentage', value: cardLimitPercentage || 100 },
        {
          name: 'income',
          value: formatAmount(income),
        },
        {
          name: 'otherIncome',
          value: formatAmount(otherIncome),
        },
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    } else if (customerId) {
      const { data } = await Scoring.getCustomerAdditionalJobInfo();
      if (data.result === 'OK') {
        const jobTitle = jobTitles.find((job) => job.value === data.jobTitleCode);
        const occupation = occupations.find((occ) => occ.value === data.occupationCode);
        const jobSector = jobSectors.find((job) => job.value === data.jobSectorCode);
        const businessLine = businessLines.find((buss) => buss.value === data.businessLineCode);
        const employeeNumber = employeeNumbers.find((emp) => emp.value === data.employeeNumberCode);
        const is2NdflCertificateProvided = yesNoComboItems.find(
          (item) => item.value === data.is2NdflCertificateProvided
        );
        const workLifeBeginningMonth =
          months.find((month) => month.value === data.workLifeBeginningMonth) || null;
        const workLifeBeginningYear =
          years.find((year) => year.value === data.workLifeBeginningYear) || null;
        const currentWorkBeginningMonth =
          months.find((month) => month.value === data.currentWorkBeginningMonth) || null;
        const currentWorkBeginningYear =
          months.find((year) => year.value === data.currentWorkBeginningYear) || null;
        [
          { name: 'companyName', value: data.companyName },
          { name: 'workInn', value: data.workInn },
          { name: 'jobTitle', value: jobTitle },
          { name: 'occupation', value: occupation },
          { name: 'jobSector', value: jobSector },
          { name: 'businessLine', value: businessLine },
          { name: 'jobNumberForLast3Years', value: data.jobNumberForLast3Years },
          { name: 'employeeNumber', value: employeeNumber },
          { name: 'is2NdflCertificateProvided', value: is2NdflCertificateProvided },
          { name: 'requestedLimit', value: formatAmount(data.requestedLimit) },
          { name: 'workLifeBeginningMonth', value: workLifeBeginningMonth },
          { name: 'workLifeBeginningYear', value: workLifeBeginningYear },
          { name: 'currentWorkBeginningMonth', value: currentWorkBeginningMonth },
          { name: 'currentWorkBeginningYear', value: currentWorkBeginningYear },
          {
            name: 'isGovernmentOrganisation',
            value: data.isGovernmentOrganisation
              ? OrganizationType.STATE
              : OrganizationType.NON_STATE,
          },
          {
            name: 'income',
            value: formatAmount(data.income),
          },
          {
            name: 'otherIncome',
            value: formatAmount(data.otherIncome),
          },
        ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
      }
    }
    setIsLoading(false);
  }, [
    businessLines,
    customerId,
    employeeNumbers,
    jobSectors,
    jobTitles,
    months,
    occupations,
    setValue,
    years,
    yesNoComboItems,
  ]);

  useEffect(() => {
    if (
      jobTitles.length &&
      occupations.length &&
      jobSectors.length &&
      businessLines.length &&
      employeeNumbers.length &&
      yesNoComboItems.length &&
      years.length &&
      months.length
    ) {
      loadScoringInformation();
    }
  }, [
    jobTitles.length,
    occupations.length,
    jobSectors.length,
    businessLines.length,
    yesNoComboItems.length,
    employeeNumbers.length,
    loadScoringInformation,
    jobTitles,
    occupations,
    jobSectors,
    businessLines,
    employeeNumbers,
    yesNoComboItems,
    years.length,
    months.length,
  ]);

  const onSubmit = async (form: FormData): Promise<void> => {
    const { data } = await ScoringJobPageValidation.validateScoringJobPageData({
      companyName: form.companyName,
      workInn: form.workInn,
      jobNumberForLast3Years: form.jobNumberForLast3Years,
      jobTitle: dictionaryLineFormat(form.jobTitle),
      occupation: dictionaryLineFormat(form.occupation),
      jobSector: dictionaryLineFormat(form.jobSector),
      businessLine: dictionaryLineFormat(form.businessLine),
      employeeNumber: dictionaryLineFormat(form.employeeNumber),
      is2NdflCertificateProvided: dictionaryLineFormat(form.is2NdflCertificateProvided),
      workLifeBeginningMonth: dictionaryLineFormat(form.workLifeBeginningMonth),
      workLifeBeginningYear: dictionaryLineFormat(form.workLifeBeginningYear),
      currentWorkBeginningMonth: dictionaryLineFormat(form.currentWorkBeginningMonth),
      currentWorkBeginningYear: dictionaryLineFormat(form.currentWorkBeginningYear),
      isGovernmentOrganisation: form.isGovernmentOrganisation === OrganizationType.STATE,
      ...(limitDisplay?.isRequestedLimitVisible
        ? { requestedLimit: Number(formatBlank(form.requestedLimit)) }
        : {}),
      ...(limitDisplay?.isCardLimitPercentageVisible
        ? { cardLimitPercentage: form.cardLimitPercentage }
        : {}),
      income: Number(formatBlank(form.income)),
      otherIncome: Number(formatBlank(form.otherIncome)),
    });

    if (data.result === 'OK') {
      goToNextStep(customerId);
    }
  };
  const fetchEntitiesSuggestions = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 4) return;
    const { data } = await Fns.search({ q: searchQuery });
    setEntitySuggestions(data?.items ?? []);
  }, [searchQuery]);

  const debouncedSearch = useDebounce(fetchEntitiesSuggestions);
  const handleCompanyChange = (value: string): void => {
    setSearchQuery(value);
    // setValue('companyName', value, { shouldValidate: true });
    setValue('workInn', '', { shouldValidate: true });
  };
  const handleEntitySelect = (entity: LegalEntity): void => {
    setValue('companyName', entity.shortName, { shouldValidate: true });
    setValue('workInn', entity.inn, { shouldValidate: true });
    setSearchQuery('');
  };

  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    debouncedSearch();
  }, [debouncedSearch, fetchEntitiesSuggestions]);
  const occupation = watch('occupation');

  const isInnRequired = !isInnNotRequiredOccupation(occupation);
  const isOnlyJobsRequired = !isNotJobOccupation(occupation);

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_jobScoring_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_jobScoring_jobInfo_subtitle" />
      </Subtitle>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="occupation"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.occupation as FieldError)?.message as string)}
                  items={occupations}
                  label={t('cardEntry_jobScoring_employmentType_label')}
                  name="occupation"
                  onChange={onChange}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="companyName"
              render={({ onChange, value }) => (
                <>
                  <SuggestionDropdown
                    disabled={disablePage}
                    errorMessage={t(errors?.companyName?.message as string)}
                    items={entitySuggestions}
                    label={t('cardEntry_jobScoring_companyName_label')}
                    name="companyName"
                    onChange={(e) => {
                      onChange(e);
                      handleCompanyChange(value);
                    }}
                    onItemSelect={handleEntitySelect}
                    required={isOnlyJobsRequired}
                    value={value}
                  />
                </>
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="workInn"
              render={({ onChange, value }) => (
                <TextInput
                  disabled={disablePage}
                  errorMessage={t(errors?.workInn?.message as string)}
                  formatter={formatNumeric}
                  label={t('cardEntry_jobScoring_workInn_label')}
                  maxLength={12}
                  name="workInn"
                  onChange={(e) => {
                    onChange(e);
                    trigger('workInn');
                  }}
                  required={isOnlyJobsRequired && isInnRequired}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="jobSector"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.jobSector as FieldError)?.message as string)}
                  items={jobSectors}
                  label={t('cardEntry_jobScoring_affiliationOfOrganization_label')}
                  name="jobSector"
                  onChange={onChange}
                  required={isOnlyJobsRequired}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="businessLine"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.businessLine as FieldError)?.message as string)}
                  items={businessLines}
                  label={t('cardEntry_jobScoring_lineOfBusiness_label')}
                  name="businessLine"
                  onChange={onChange}
                  required={isOnlyJobsRequired}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="jobTitle"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.jobTitle as FieldError)?.message as string)}
                  items={jobTitles}
                  label={t('cardEntry_jobScoring_position_label')}
                  name="jobTitle"
                  onChange={onChange}
                  required={isOnlyJobsRequired}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="employeeNumber"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.employeeNumber as FieldError)?.message as string)}
                  items={employeeNumbers}
                  label={t('cardEntry_jobScoring_numberOfEmployees_label')}
                  name="employeeNumber"
                  onChange={onChange}
                  required={isOnlyJobsRequired}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="jobNumberForLast3Years"
              render={({ onChange, value }) => (
                <TextInput
                  disabled={disablePage}
                  errorMessage={t(errors?.jobNumberForLast3Years?.message as string)}
                  formatter={formatNumeric}
                  label={t('cardEntry_jobScoring_jobsInLast3Years_label')}
                  maxLength={2}
                  name="jobNumberForLast3Years"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="is2NdflCertificateProvided"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t(
                    (errors?.is2NdflCertificateProvided as FieldError)?.message as string
                  )}
                  items={yesNoComboItems}
                  label={t('cardEntry_jobScoring_certificate2NDFL_label')}
                  name="is2NdflCertificateProvided"
                  onChange={onChange}
                  required={isOnlyJobsRequired}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Separator />
            <CustomLabel>
              <Trans i18nKey="cardEntry_jobScoring_organizationFoundationDate_text" />
            </CustomLabel>
            <TwoComboContainer>
              <MonthContainer>
                <Controller
                  control={control}
                  name="currentWorkBeginningMonth"
                  render={({ onChange, value }) => (
                    <Combobox
                      disabled={disablePage}
                      errorMessage={t(
                        (errors?.currentWorkBeginningMonth as FieldError)?.message as string
                      )}
                      items={months}
                      label={t('general_month_label')}
                      name="currentWorkBeginningMonth"
                      onChange={onChange}
                      required={isOnlyJobsRequired}
                      value={value}
                    />
                  )}
                />
              </MonthContainer>
              <YearContainer>
                <Controller
                  control={control}
                  name="currentWorkBeginningYear"
                  render={({ onChange, value }) => (
                    <Combobox
                      disabled={disablePage}
                      errorMessage={t(
                        (errors?.currentWorkBeginningYear as FieldError)?.message as string
                      )}
                      items={years}
                      label={t('general_year_label')}
                      name="currentWorkBeginningYear"
                      onChange={onChange}
                      required={isOnlyJobsRequired}
                      value={value}
                      searchable
                    />
                  )}
                />
              </YearContainer>
            </TwoComboContainer>
          </FormElement>
          <FormElement>
            <Separator />
            <CustomLabel>
              <Trans i18nKey="cardEntry_jobScoring_workStartingDate_text" />
            </CustomLabel>
            <TwoComboContainer>
              <MonthContainer>
                <Controller
                  control={control}
                  name="workLifeBeginningMonth"
                  render={({ onChange, value }) => (
                    <Combobox
                      disabled={disablePage}
                      errorMessage={t(
                        (errors?.workLifeBeginningMonth as FieldError)?.message as string
                      )}
                      items={months}
                      label={t('general_month_label')}
                      name="workLifeBeginningMonth"
                      onChange={onChange}
                      value={value}
                      required
                    />
                  )}
                />
              </MonthContainer>
              <YearContainer>
                <Controller
                  control={control}
                  name="workLifeBeginningYear"
                  render={({ onChange, value }) => (
                    <Combobox
                      disabled={disablePage}
                      errorMessage={t(
                        (errors?.workLifeBeginningYear as FieldError)?.message as string
                      )}
                      items={years}
                      label={t('general_year_label')}
                      name="workLifeBeginningYear"
                      onChange={onChange}
                      value={value}
                      required
                      searchable
                    />
                  )}
                />
              </YearContainer>
            </TwoComboContainer>
          </FormElement>
        </FormRow>
        <Separator />
        <CustomLabel>
          <Trans i18nKey="cardEntry_jobScoring_organizationTypeRelationToState_label" />
        </CustomLabel>
        <FormRow>
          <LineFormElement>
            <Controller
              control={control}
              name="isGovernmentOrganisation"
              render={({ onChange, value }) => (
                <RadioButtonGroup
                  disabled={disablePage}
                  errorMessage={t(errors.isGovernmentOrganisation?.message as string)}
                  onChange={onChange}
                  value={value}
                  required
                >
                  <RadioButton
                    label={t('cardEntry_jobScoring_state_label')}
                    name={OrganizationType.STATE}
                  />
                  <RadioButton
                    label={t('cardEntry_jobScoring_nonState_label')}
                    name={OrganizationType.NON_STATE}
                  />
                </RadioButtonGroup>
              )}
            />
          </LineFormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="cardEntry_jobScoring_limitInfo_subtitle" />
        </Subtitle>
        <FormRow>
          {limitDisplay?.isRequestedLimitVisible ? (
            <LineFormElement>
              <Controller
                control={control}
                name="requestedLimit"
                render={({ onChange, value }) => (
                  <CurrencyInput
                    currencySymbol="₽"
                    disabled={disablePage}
                    errorMessage={t(errors?.requestedLimit?.message as string)}
                    label={t('cardEntry_jobScoring_requestedCreditLimit_label')}
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </LineFormElement>
          ) : null}
          {limitDisplay?.isCardLimitPercentageVisible ? (
            <FormElement>
              <Controller
                control={control}
                name="cardLimitPercentage"
                render={({ onChange, value }) => (
                  <Slider
                    disabled={disablePage}
                    errorMessage={t(errors?.cardLimitPercentage?.message as string)}
                    label={t('cardEntry_jobScoring_cardLimitMultiplier_label')}
                    maxLimit={100}
                    minLimit={0}
                    onChange={onChange}
                    step={1}
                    value={value}
                    required
                  />
                )}
              />
            </FormElement>
          ) : null}
        </FormRow>
        <FormRow>
          <LineFormElement>
            <Controller
              control={control}
              name="income"
              render={({ onChange, value }) => (
                <CurrencyInput
                  currencySymbol="₽"
                  disabled={disablePage}
                  errorMessage={t(errors?.income?.message as string)}
                  label={t('cardEntry_jobScoring_monthlyIncome_label')}
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </LineFormElement>
        </FormRow>
        <FormRow>
          <LineFormElement>
            <Controller
              control={control}
              name="otherIncome"
              render={({ onChange, value }) => (
                <CurrencyInput
                  currencySymbol="₽"
                  disabled={disablePage}
                  errorMessage={t(errors?.otherIncome?.message as string)}
                  label={t('cardEntry_jobScoring_otherSourcesMonthlyIncome_label')}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </LineFormElement>
        </FormRow>
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default ScoringInformation;
